import { MouseEvent as ReactMouseEvent, PointerEvent, useCallback, useRef, useState } from 'react';

const DRAG_THRESHOLD_PX = 6;

/**
 * גרירת עכבר לגלול אופקית (כולל RTL). מגע נשאר לגלילה טבעית.
 * אחרי גרירה — בולמים קליק כדי שלא ייפתח כרטיס בטעות.
 */
export const useHorizontalDragScroll = () => {
    const ref = useRef<HTMLDivElement | null>(null);
    const drag = useRef({
        pointerId: -1,
        startX: 0,
        lastX: 0,
        moved: false,
        rtl: false
    });
    const [dragging, setDragging] = useState(false);

    const onPointerDownCapture = useCallback((event: PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        const el = ref.current;
        if (!el) return;
        drag.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            lastX: event.clientX,
            moved: false,
            rtl: getComputedStyle(el).direction === 'rtl'
        };
        try {
            el.setPointerCapture(event.pointerId);
        } catch {
            /* ignore */
        }
    }, []);

    const onPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
        if (drag.current.pointerId !== event.pointerId) return;
        const el = ref.current;
        if (!el) return;
        const dx = event.clientX - drag.current.lastX;
        drag.current.lastX = event.clientX;
        if (dx === 0) return;
        el.scrollLeft += drag.current.rtl ? dx : -dx;
        if (Math.abs(event.clientX - drag.current.startX) >= DRAG_THRESHOLD_PX) {
            drag.current.moved = true;
            setDragging(true);
            event.preventDefault();
        }
    }, []);

    const finish = useCallback((event: PointerEvent<HTMLDivElement>) => {
        if (drag.current.pointerId !== event.pointerId) return;
        const el = ref.current;
        if (el) {
            try {
                el.releasePointerCapture(event.pointerId);
            } catch {
                /* already released */
            }
        }
        drag.current.pointerId = -1;
        setDragging(false);
        window.setTimeout(() => {
            drag.current.moved = false;
        }, 0);
    }, []);

    const onClickCapture = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
        if (!drag.current.moved) return;
        event.preventDefault();
        event.stopPropagation();
        drag.current.moved = false;
    }, []);

    return {
        ref,
        dragging,
        onPointerDownCapture,
        onPointerMove,
        onPointerUp: finish,
        onPointerCancel: finish,
        onClickCapture
    };
};
